import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, IconButton, Modal } from "@mui/material";
import SchoolProfileSkeleton from './SchoolProfileSkeleton';
import axiosInstance from '../axiosConfig';
import EditIcon from "@mui/icons-material/Edit";
import API_URL from "../config";

const FRAME_WIDTH = 1500; // Breite des nutzbaren SVG-Rahmens
const MAX_LINES = 24;

const measureTextWidth = (text, font) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  return context.measureText(text).width;
};

const formatText = (text, maxWidth, font) => {
  if (text.trim() === "") {
    return [""]; // Leere Zeile explizit zurückgeben
  }

  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const lineWidth = measureTextWidth(testLine, font);

    if (lineWidth <= maxWidth) {
      currentLine = testLine; // Zeile bleibt gleich
    } else {
      lines.push(currentLine); // Zeile abschließen
      currentLine = word; // Neues Wort starten
    }
  });

  if (currentLine) {
    lines.push(currentLine); // Letzte Zeile hinzufügen
  }

  return lines;
};

const SchoolProfilePhilosophy = ({ onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [text, setText] = useState("");
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const [isPreviewAvailable, setisPreviewAvailable] = useState(false);

  useEffect(() => {
    // Daten aus der Datenbank laden
    const token = localStorage.getItem("authToken");
    axiosInstance
      .get('/profile/school-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolProfile(response.data.profile);
          const initialText = response.data.profile.philosophyMessage || "";
          updateTextAndLines(initialText);
        }
      })
      .catch((error) => {
        console.error("Fehler beim Abrufen des Schulprofils:", error);
      });
  }, []);

  useEffect(() => {
    if (schoolProfile?.philosophyMessage) {
      fetch(`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Schulphilosophie.png`)
        .then((response) => {
          if (response.ok) {
            setisPreviewAvailable(true);
          } else {
            setisPreviewAvailable(false);
          }
        })
        .catch((error) => {
          console.error('Fehler beim Überprüfen des Schulphilosophiepfades:', error);
          setisPreviewAvailable(false);
        });
    }
  }, [schoolProfile?.philosophyMessage]);

  const calculateFormattedLines = (rawText) => {
    const font = "42px Arial"; // SVG-Schriftgröße für Philosophie
    const manualLines = rawText.split("\n");

    let formattedLines = [];
    manualLines.forEach((line) => {
      const wrappedLines = formatText(line || " ", FRAME_WIDTH, font); // Leere Zeilen auch verarbeiten
      formattedLines = formattedLines.concat(wrappedLines);
    });

    return formattedLines;
  };

  const updateTextAndLines = (rawText) => {
    const formattedLines = calculateFormattedLines(rawText);
    setText(rawText);
    setLines(formattedLines);
    setError(formattedLines.length > MAX_LINES); // Fehler, wenn mehr als 24 Zeilen
  };

  const handleTextChange = (event) => {
    onUpdate({ type: "editStart" });
    const rawText = event.target.value;
    updateTextAndLines(rawText);
  };

  const handleSave = () => {
    if (error) {
      alert("Der Text überschreitet die maximale Zeilenanzahl. Bitte kürzen.");
      return;
    }

    const token = localStorage.getItem("authToken");
    const savedText = lines.join("\n");

    axiosInstance
      .post(
        '/profile/school-philosophy',
        {
          philosophyMessage: savedText,
        },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      )
      .then((response) => {
        setSchoolProfile(response.data.profile);
        setImageTimestamp(Date.now());
        onUpdate({ type: "saveSuccess", profile: response.data.profile } );
        setIsEditing(false);
      })
      .catch((error) => {
        console.error("Fehler beim Speichern des Schulprofils:", error);
        alert("Fehler beim Speichern der Schulphilosophie. Bitte versuchen Sie es erneut.");
      });
  };

  return (
    <Box
      sx={{
        position: "relative",
        padding: "1rem",
      }}
    >
      <Typography variant="h5">Schulphilosophie</Typography>

      {!isEditing && (
        <Button
          variant="contained"
          size="small"
          color='primary'
          sx={{
            backgroundColor: 'primary.light',
            color: '#fff',
            position: 'absolute',
            top: '1rem',
            right: '2rem',
            border: 'none',
            '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
          }}
          onClick={() => {
            setIsEditing(true);
            const initialText = schoolProfile?.philosophyMessage || "";
            updateTextAndLines(initialText); // Text und Zeilen aktualisieren
          }}
        >
          Bearbeiten
        </Button>
      )}

      {!isEditing ? (
        <Box sx={{ marginBottom: "1rem" }}>
          <Typography color="black" variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Vorschau Schulphilosophie
          </Typography>
          {schoolProfile?.philosophyMessage && isPreviewAvailable ? (
            <Box
              sx={{
                cursor: "pointer",
                display: "inline-block",
                marginBottom: "1rem",
                border: "1px solid #ccc",
                borderRadius: "8px",
                overflow: "hidden",
              }}
              onClick={() => setShowPopup(true)}
            >
              <img
                src={`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Schulphilosophie.png?timestamp=${imageTimestamp}`}
                alt="Philosophie Vorschau"
                style={{ width: "100%", maxWidth: "300px" }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Sie müssen zunächst die Formularfelder ausfüllen und speichern, damit eine Vorschau für Sie generiert werden kann.
            </Typography>
          )}
          <Typography color="black" variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Schulphilosophie
          </Typography>
          <Typography
            style={{ whiteSpace: "pre-line" }}
            sx={{ color: schoolProfile?.philosophyMessage ? "inherit" : "gray" }}
          >
            {schoolProfile?.philosophyMessage || "Nicht ausgefüllt"}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Ihr Text
          </Typography>
          <TextField
            multiline
            fullWidth
            value={text}
            onChange={handleTextChange}
            rows={24}
            inputProps={{ style: { fontFamily: "Arial", fontSize: "16px", lineHeight: "20px" } }}
            sx={{
              marginBottom: "0.5rem",
              border: error ? "1px solid red" : "none",
            }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color={error ? "error" : "textSecondary"}>
              {error
                ? "Die maximale Zeilenanzahl wurde überschritten!"
                : `Verbleibende Zeilen: ${Math.max(0, 24 - lines.length)}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Maximale Zeilen: 24
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleSave}
            sx={{ mt: 2, bgcolor: 'primary.light', '&:hover': { color: '#fff' }  }}
            disabled={error}
          >
            Speichern
          </Button>
        </Box>
      )}
      {showPopup && (
        <Modal
          open={showPopup}
          onClose={() => setShowPopup(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "80%",
              maxWidth: "600px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: 24,
              padding: "1.5rem",
              position: "relative",
              textAlign: "center",
            }}
          >
            <IconButton
              sx={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
              }}
              onClick={() => setShowPopup(false)}
            >
              X
            </IconButton>
            <img
              src={`${API_URL}/uploads/school-profiles/${schoolProfile._id}/components/png/Schulphilosophie.png?timestamp=${imageTimestamp}`}
              alt="Philosophie Vorschau"
              style={{
                width: "100%",
                borderRadius: "8px",
              }}
            />
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default SchoolProfilePhilosophy;
